import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { UiBreadcrumb, UiDataTable } from 'components/common'
import { Box, Button, Flex, Heading, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, useDisclosure } from '@chakra-ui/react'
import { deleteCustomer, getCustomers, migrateLatestZohoContacts, exportCustomers } from 'api'
import { Link as NavLink, useNavigate } from 'react-router-dom'
import { MdAdd } from 'react-icons/md'
import { useAppContext } from 'hooks/useAppContext'
import { ModalNotes } from './ModalNotes'
import { format as formatDate } from 'date-fns'

const BreadcrumbNav = [
  {
    label: 'Customers',
    isCurrentPage: true
  }
]

const CUSTOMERS_COLUMNS = [
  {
    label: 'Company',
    orderBy: 'company',
    render: ({ company }) => company
  },
  {
    label: 'Contact Name',
    orderBy: 'name',
    render: ({ name }) => name
  },
  {
    label: 'Email',
    orderBy: 'email',
    render: ({ email }) => email
  },
  {
    label: 'Phone',
    orderBy: 'phone',
    render: ({ phone }) => '+1 ' + phone
  },
  {
    label: 'Contact Owner',
    orderBy: 'salesUser',
    render: ({ salesUser }) => salesUser
  },
  {
    label: 'Source ID',
    orderBy: 'zohoSourceId',
    render: ({ zohoSourceId }) => zohoSourceId
  },
  {
    label: 'Created At (Zoho)',
    orderBy: 'createdAtZoho',
    render: ({ createdAtZoho }) => {
      const date = new Date(createdAtZoho)
      return createdAtZoho ? formatDate(new Date(date.getTime() + date.getTimezoneOffset() * 60000), 'MM/dd/yyyy') : ''
    }
  },
  {
    label: 'Actions',
    render: ({ opportunities, notesCount, onRemove, onNotes, onOpportunities, ...rest }) => (
      <Flex columnGap={2}>
        <Button size="xs" colorScheme="green" onClick={() => onOpportunities({ opportunities, ...rest })}>{`Opportunities (${opportunities.length})`}</Button>
        <Button size="xs" colorScheme="gray" onClick={() => onNotes(rest)}>{`Notes (${notesCount})`}</Button>
        <Button as={NavLink} to={`/customers/edit/${rest.id}`} size="xs" colorScheme="blue">Edit</Button>
        <Button size="xs" colorScheme="red" onClick={() => onRemove(rest)}>Remove</Button>
      </Flex>
    )
  }
]

export const CustomerList = () => {
  const navigate = useNavigate()
  const disclosureRemove = useDisclosure()
  const disclosureNotes = useDisclosure()
  const disclosureOpportunities = useDisclosure()
  const { createToast } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 10,
    search: '',
    orderBy: {
      key: 'createdAtZoho',
      direction: 'desc'
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [isMigratingZohoContacts, setIsMigratingZohoContacts] = useState(false)
  const [rows, setRows] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null)

  const load = async () => {
    setIsLoading(true)
    try {
      const response = await getCustomers(
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction
      )
      setRows(response.rows)
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const onRemove = (customer) => {
    setSelectedCustomer(customer)
    disclosureRemove.onOpen()
  }

  const handleRemove = async (customerId) => {
    setIsRemoving(true)
    try {
      await deleteCustomer(customerId)
      setIsRemoving(false)
      disclosureRemove.onClose()
      load()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsRemoving(false)
    }
  }

  const onNotes = (customer) => {
    setSelectedCustomer(customer)
    disclosureNotes.onOpen()
  }

  const onOpportunities = (customer) => {
    setSelectedCustomer(customer)
    disclosureOpportunities.onOpen()
  }

  const handleMigrateZohoContacts = async () => {
    setIsMigratingZohoContacts(true)
    try {
      await migrateLatestZohoContacts()
      createToast('success', 'Success', 'Migrated latest zoho contacts')
      setIsMigratingZohoContacts(false)
    } catch {
      createToast('error', 'Error', 'Failed to migrate latest zoho contacts')
      setIsMigratingZohoContacts(false)
    }
  }

  const customizedRows = useMemo(() => {
    return rows.map(v => ({ ...v, onRemove, onNotes, onOpportunities }))
  }, [rows])

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Customers</Heading>
          <Flex gap={4}>
            <Button
              size="md"
              colorScheme="green"
              onClick={handleMigrateZohoContacts}
              leftIcon={<MdAdd size="24px" color="white" />}
              isLoading={isMigratingZohoContacts}
            >
              Migrate from Zoho
            </Button>
            <Button
              size="md"
              colorScheme="blue"
              onClick={() => navigate('/customers/add')}
              leftIcon={<MdAdd size="24px" color="white" />}
            >
              Add Customer
            </Button>
          </Flex>
        </Flex>
        <UiDataTable
          tableConfig={tableConfig}
          updateTableConfig={setTableConfig}
          rows={customizedRows}
          rowKey="id"
          columns={CUSTOMERS_COLUMNS}
          isLoading={isLoading}
          exportConfig={{ filename: 'customers.csv', request: exportCustomers }}
        />
      </Box>
      {disclosureRemove.isOpen && (
        <ModalRemoveCustomer
          customer={selectedCustomer}
          isRemoving={isRemoving}
          handleRemove={() => handleRemove(selectedCustomer?.id)}
          {...disclosureRemove}
        />
      )}
      {disclosureNotes.isOpen && (
        <ModalNotes
          customer={selectedCustomer}
          reload={load}
          {...disclosureNotes}
        />
      )}
      {disclosureOpportunities.isOpen && (
        <ModalOpportunities
          customer={selectedCustomer}
          {...disclosureOpportunities}
        />
      )}
    </>
  )
}

const ModalOpportunities = ({ customer, isOpen, onClose }) => {
  const onClick = (opportunityId) => {
    window.open(`/opportunities/view/${opportunityId}`, '_blank')
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{customer?.name} - Related Opportunities</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {customer.opportunities.length > 0 ? (
            customer.opportunities.map(v => {
              return <Box key={v.id} my={2}><Button size="sm"variant="link" colorScheme="blue" onClick={() => onClick(v.id)}>{v.name}</Button></Box>
            })
          ) : (
            <Text>No opportunities.</Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant='ghost' onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

ModalOpportunities.propTypes = {
  customer: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}

const ModalRemoveCustomer = ({ customer, isRemoving, handleRemove, isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Remove Customer</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>Are you sure to remove the customer - {customer?.name}?</Text>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='red' mr={3} onClick={handleRemove} isLoading={isRemoving}>
            Remove
          </Button>
          <Button variant='ghost' onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

ModalRemoveCustomer.propTypes = {
  customer: PropTypes.object,
  isRemoving: PropTypes.bool,
  handleRemove: PropTypes.func,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}
